/**
 * DALY BMS status and fault bit-field parsers
 */

/**
 * Parse DALY Charge/Discharge Status (Register 0x48)
 * @param {number} value - 0: Stand Still, 1: Charging, 2: Discharging
 * @returns {string} Status string
 */
export function parseDalyChargeDischargeStatus(value) {
  switch (value) {
    case 0:
      return 'Stand Still';
    case 1:
      return 'Charging';
    case 2:
      return 'Discharging';
    default:
      return 'Unknown';
  }
}

const FAULT1_MAP = [
  { bit: 0, label: 'Cell Overvoltage Level 1' },
  { bit: 1, label: 'Cell Overvoltage Level 2' },
  { bit: 2, label: 'Cell Undervoltage Level 1' },
  { bit: 3, label: 'Cell Undervoltage Level 2' },
  { bit: 4, label: 'Total Overvoltage Level 1' },
  { bit: 5, label: 'Total Overvoltage Level 2' },
  { bit: 6, label: 'Total Undervoltage Level 1' },
  { bit: 7, label: 'Total Undervoltage Level 2' },
  { bit: 8, label: 'Charge Overtemperature Level 1' },
  { bit: 9, label: 'Charge Overtemperature Level 2' },
  { bit: 10, label: 'Charge Undertemperature Level 1' },
  { bit: 11, label: 'Charge Undertemperature Level 2' },
  { bit: 12, label: 'Discharge Overtemperature Level 1' },
  { bit: 13, label: 'Discharge Overtemperature Level 2' },
  { bit: 14, label: 'Discharge Undertemperature Level 1' },
  { bit: 15, label: 'Discharge Undertemperature Level 2' },
];

const FAULT2_MAP = [
  { bit: 0, label: 'Charge Overcurrent Level 1' },
  { bit: 1, label: 'Charge Overcurrent Level 2' },
  { bit: 2, label: 'Discharge Overcurrent Level 1' },
  { bit: 3, label: 'Discharge Overcurrent Level 2' },
  { bit: 4, label: 'SOC High Level 1' },
  { bit: 5, label: 'SOC High Level 2' },
  { bit: 6, label: 'SOC Low Level 1' },
  { bit: 7, label: 'SOC Low Level 2' },
  { bit: 8, label: 'Differential Voltage Level 1' },
  { bit: 9, label: 'Differential Voltage Level 2' },
  { bit: 10, label: 'Differential Temperature Level 1' },
  { bit: 11, label: 'Differential Temperature Level 2' },
  { bit: 12, label: 'MOS Overtemperature Level 1' },
  { bit: 13, label: 'MOS Overtemperature Level 2' },
  { bit: 14, label: 'Environment Overtemperature Level 1' },
  { bit: 15, label: 'Environment Overtemperature Level 2' },
];

const FAULT3_MAP = [
  { bit: 0, label: 'Environment Undertemperature Level 1' },
  { bit: 1, label: 'Environment Undertemperature Level 2' },
  { bit: 2, label: 'Heating Overtemperature Level 1' },
  { bit: 3, label: 'Heating Overtemperature Level 2' },
  { bit: 4, label: 'MOS Temperature Sensor Fault' },
  { bit: 5, label: 'Environment Temperature Sensor Fault' },
  { bit: 6, label: 'Battery Temperature Sensor Fault' },
  { bit: 7, label: 'Heating Temperature Sensor Fault' },
  { bit: 8, label: 'Charge MOS Fault' },
  { bit: 9, label: 'Discharge MOS Fault' },
  { bit: 10, label: 'Voltage Sampling Fault' },
  { bit: 11, label: 'Current Sampling Fault' },
  { bit: 12, label: 'Short Circuit Protection' },
  { bit: 13, label: 'AFE Chip Fault' },
  { bit: 14, label: 'EEPROM Fault' },
  { bit: 15, label: 'RTC Fault' },
];

const FAULT4_MAP = [
  { bit: 0, label: 'Precharge Fault' },
  { bit: 1, label: 'Vehicle Communication Fault' },
  { bit: 2, label: 'Intranet Communication Fault' },
  { bit: 3, label: 'Residual Capacity Fault' },
  { bit: 4, label: 'Current Sensor Fault' },
  { bit: 5, label: 'Main Contactor Fault' },
  { bit: 6, label: 'Precharge Contactor Fault' },
];

function parseBits(value, bitMap, defaultSentinel) {
  if (!value || typeof value !== 'number') {
    return [defaultSentinel];
  }
  const events = [];
  for (const { bit, label } of bitMap) {
    if ((value & (1 << bit)) !== 0) {
      events.push(label);
    }
  }
  return events.length > 0 ? events : [defaultSentinel];
}

/**
 * Parse DALY Fault Status 1 (Register 0x66)
 * Sentinel: ['No Alarms']
 */
export function parseDalyFault1(value) {
  return parseBits(value, FAULT1_MAP, 'No Alarms');
}

/**
 * Parse DALY Fault Status 2 (Register 0x67)
 * Sentinel: ['No Protection Events']
 */
export function parseDalyFault2(value) {
  return parseBits(value, FAULT2_MAP, 'No Protection Events');
}

/**
 * Parse DALY Fault Status 3 (Register 0x68)
 * Sentinel: ['No Protection Events']
 */
export function parseDalyFault3(value) {
  return parseBits(value, FAULT3_MAP, 'No Protection Events');
}

/**
 * Parse DALY Fault Status 4 (Register 0x69)
 * Sentinel: ['No Protection Events']
 */
export function parseDalyFault4(value) {
  return parseBits(value, FAULT4_MAP, 'No Protection Events');
}
